module Client.Supplies
open Elmish
open Fable.Core
open Fetch.Types
open Shared
open System
open Feliz
open Feliz.UseElmish
open Feliz.MaterialUI
open Fable.MaterialUI.Icons
open Fable.MaterialUI.MaterialDesignIcons
open Thoth.Fetch
open Feliz.SweetAlert
open Fable.Core.JS
open Fable.Core.JsInterop
open Components.SuppliesForm
open CustomerDexImaging.Models

type Msg =
  | EquipmentLookupResult of Result<Equipment,Components.EquipmentLookup.EquipmentLookupFailureResult>
  | EquipmentSelected of Shared.Equipment
  | ContactEntryCompleted of ContactSelectionInfo
  | PurchaseOrderNumberEntryCompleted of string
  | SupplyCategoriesLookupResult of Result<SupplyCategory list, string>
  | SupplyCategoriesSelected of SupplyCategory list
  | LookupOrders
  | SupplyOrderInfoLookupResult of Result<Shared.SupplyOrderInfos,string>
  | AcknowledgeExistingOrders
  | PlaceOrder of Shared.SupplyRequest
  | SupplyRequestResult of Result<string, string>
  | ResetState
  | MpsScanRequested of Shared.Equipment
  | MpsScanLookupResult of MpsScan option

type MpsScanState =
  | RequestPending
  | NoMpsScanFound
  | MpsScanFound of MpsScan
    member this.toOption () =
      match this with
      | MpsScanState.NoMpsScanFound -> None
      | MpsScanState.RequestPending -> None
      | MpsScanState.MpsScanFound v -> Some v

type ContactEntryState =
  { Equipment : Shared.Equipment
    MpsScanState : MpsScanState
    Contact : ContactSelectionInfo option }

type PurchaseOrderEntryState =
  { Equipment : Shared.Equipment
    MpsScanState : MpsScanState
    Contact : ContactSelectionInfo
    PurchaseOrderNumber : string option }

type SupplyIdentificationState =
  { Equipment : Shared.Equipment
    MpsScanState : MpsScanState
    Contact : ContactSelectionInfo
    PurchaseOrderNumber : string option }

type SupplyRequestEntryState =
  { Equipment : Shared.Equipment
    MpsScanState : MpsScanState
    Contact : ContactSelectionInfo
    SupplyCategories : SupplyCategory list
    AvailableSupplyCategories : SupplyCategory list
    OtherSupplyCategories : SupplyCategory list
    SupplyOrderInfos : Shared.SupplyOrderInfos option
    PurchaseOrderNumber : string option }

type OrderSuccessResultState =
  { QueueResponse : string
    SupplyRequestEntryState : SupplyRequestEntryState }

type OrderFailureResultState =
  { FailureMessage : string
    SupplyRequestEntryState : SupplyRequestEntryState }

type Model =
  | EquipmentLookup
  | ContactEntry of ContactEntryState
  | PurchaseOrderEntry of PurchaseOrderEntryState
  | SupplyIdentification of SupplyIdentificationState
  | SupplySelection of SupplyRequestEntryState
  | SupplyOrderInfoLookup of SupplyRequestEntryState
  | ExistingOrdersWarning of SupplyRequestEntryState
  | PlacingOrder of SupplyRequestEntryState
  | OrderResult of OrderSuccessResultState
  | DowngradedForm of Components.SuppliesForm.EntryState

let orderSupplies (token, (request : Shared.SupplyRequest)) : Promise<string> =
  promise {
    let decoder = Services.decoder<string>
    let extra = Services.myExtraCoders
    let authHeader = HttpRequestHeaders.Authorization <| sprintf "Bearer %s" token
    return! Fetch.post<_,string>("/api/Supplies/Order", request, extra = extra, decoder = decoder,headers = [authHeader] )
  }

let orderSuppliesCmd token request =
  Cmd.OfPromise.either orderSupplies (token, request)
    (Ok >> SupplyRequestResult)
    (fun x ->
      "Unable to order supplies for equipment. Please retry" |> Error |> SupplyRequestResult)

let getOrderHistory (token, (request : Shared.SupplyHistoryRequest)) : Promise<Shared.SupplyOrderInfos> =
  promise {
    let decoder = Services.decoder<Shared.SupplyOrderInfos>
    let authHeader = HttpRequestHeaders.Authorization <| sprintf "Bearer %s" token
    return! Fetch.post<_,Shared.SupplyOrderInfos>("/api/Supplies/History", request, extra = Services.myExtraCoders, decoder = decoder,headers = [authHeader] )
  }

let getOrderHistoryCmd  (token, (request : Shared.SupplyHistoryRequest)) =
  Cmd.OfPromise.either getOrderHistory (token, request)
    (Ok >> SupplyOrderInfoLookupResult)
    (fun x ->
      "Unable to get order history for equipment and supply category." |> Error |> SupplyOrderInfoLookupResult)

let lookupSupplyCategories (token) : Promise<SupplyCategory list> =
  promise {
    let decoder = Services.decoder<SupplyCategory list>
    let authHeader = HttpRequestHeaders.Authorization <| sprintf "Bearer %s" token
    return! Fetch.get<_,SupplyCategory list>("/api/Equipments/SupplyCategories", decoder = decoder,headers = [authHeader] )
  }

let lookupSupplyCategoriesCmd token =
  Cmd.OfPromise.either lookupSupplyCategories token
    (Ok >> SupplyCategoriesLookupResult)
    (fun x ->
      "Error: Unable to determine supplies for equipment." |> Error |> SupplyCategoriesLookupResult)

let lookupMpsScan (token) : Promise<MpsScan option> =
  promise {
    let decoder = Services.decoder<MpsScan option>
    let authHeader = HttpRequestHeaders.Authorization <| sprintf "Bearer %s" token
    return! Fetch.get<_,MpsScan option>("/api/Equipments/MpsScan", decoder = decoder,headers = [authHeader] )
  }

let lookupMpsScanCmd token =
  Cmd.OfPromise.either lookupMpsScan token
    MpsScanLookupResult
    (fun x ->
      let msg = "Error: Unable to determine supplies levels for equipment."
      None |> MpsScanLookupResult)

let wideFormatColorSupplyCategories =
  [ SupplyCategory.BlackInk
    SupplyCategory.PhotoBlackInk
    SupplyCategory.MatteBlackInk
    SupplyCategory.LightBlackInk
    SupplyCategory.CyanInk
    SupplyCategory.LightCyanInk
    SupplyCategory.PhotoCyanInk
    SupplyCategory.PhotoLightCyanInk
    SupplyCategory.MagentaInk
    SupplyCategory.LightMagentaInk
    SupplyCategory.PhotoMagentaInk
    SupplyCategory.PhotoLightMagentaInk
    SupplyCategory.VividMagentaInk
    SupplyCategory.VividLightMagentaInk
    SupplyCategory.YellowInk
    SupplyCategory.PhotoYellowInk
    SupplyCategory.GreyInk
    SupplyCategory.LightGreyInk
    SupplyCategory.PhotoGreyInk
    SupplyCategory.WhiteInk
    SupplyCategory.OptimizerInk
    SupplyCategory.RedInk
    SupplyCategory.ChromaticRedInk
    SupplyCategory.BlueInk
    SupplyCategory.ChromaticBlueInk
    SupplyCategory.GreenInk
    SupplyCategory.ChromaticGreenInk
    SupplyCategory.MultiColorInk
    SupplyCategory.FluorescentRedInk
    SupplyCategory.PhotoLightGreyInk
    SupplyCategory.OvercoatInk
    SupplyCategory.OrangeInk
    SupplyCategory.BrownInk
    SupplyCategory.ReflexBlueInk
    SupplyCategory.GoldInk
    SupplyCategory.PurpleInk
    SupplyCategory.GlossEnhancerInk ]

let wideFormatMonoSupplyCategories =
  [ SupplyCategory.Black
    SupplyCategory.BlackInk
    SupplyCategory.PhotoBlackInk
    SupplyCategory.MatteBlackInk
    SupplyCategory.LightBlackInk ]

let monoTonerSupplyCategories =
  [ SupplyCategory.Black ]

let colorTonerSupplyCategories =
  [ SupplyCategory.Black
    SupplyCategory.Cyan
    SupplyCategory.Magenta
    SupplyCategory.Yellow ]

let allMonoSupplyCategories =
  [ SupplyCategory.Black
    SupplyCategory.BlackInk
    SupplyCategory.PhotoBlackInk
    SupplyCategory.MatteBlackInk
    SupplyCategory.LightBlackInk ]

let allColorSupplyCategories =
  [ SupplyCategory.Black
    SupplyCategory.Cyan
    SupplyCategory.Magenta
    SupplyCategory.Yellow
    SupplyCategory.BlackInk
    SupplyCategory.PhotoBlackInk
    SupplyCategory.MatteBlackInk
    SupplyCategory.LightBlackInk
    SupplyCategory.CyanInk
    SupplyCategory.LightCyanInk
    SupplyCategory.PhotoCyanInk
    SupplyCategory.PhotoLightCyanInk
    SupplyCategory.MagentaInk
    SupplyCategory.LightMagentaInk
    SupplyCategory.PhotoMagentaInk
    SupplyCategory.PhotoLightMagentaInk
    SupplyCategory.VividMagentaInk
    SupplyCategory.VividLightMagentaInk
    SupplyCategory.YellowInk
    SupplyCategory.PhotoYellowInk
    SupplyCategory.GreyInk
    SupplyCategory.LightGreyInk
    SupplyCategory.PhotoGreyInk
    SupplyCategory.WhiteInk
    SupplyCategory.OptimizerInk
    SupplyCategory.RedInk
    SupplyCategory.ChromaticRedInk
    SupplyCategory.BlueInk
    SupplyCategory.ChromaticBlueInk
    SupplyCategory.GreenInk
    SupplyCategory.ChromaticGreenInk
    SupplyCategory.MultiColorInk
    SupplyCategory.FluorescentRedInk
    SupplyCategory.PhotoLightGreyInk
    SupplyCategory.OvercoatInk
    SupplyCategory.OrangeInk
    SupplyCategory.BrownInk
    SupplyCategory.ReflexBlueInk
    SupplyCategory.GoldInk
    SupplyCategory.PurpleInk
    SupplyCategory.GlossEnhancerInk ]

let nonTonerSupplyCategories =
  [
    // In work item 16202, we have been asked to exclude drums
    //SupplyCategory.Drum
    SupplyCategory.Staples
    SupplyCategory.WasteHopper ]

let init (equipmentOption) : Model * Cmd<Msg> =
  let cmd =
    match equipmentOption with
    | Some e ->
      (Ok e) |> EquipmentLookupResult |> Cmd.ofMsg
    | None ->
      Cmd.none
  EquipmentLookup, cmd

let modelCategoryAndIsColorBasedSupplyCategories modelCategoryId isColor =
  match modelCategoryId, isColor with
  // We'll first look at the model categories
  // Wide format mono
  | 6, _ -> wideFormatMonoSupplyCategories, []
  // Wide format color
  | 27, _ -> wideFormatColorSupplyCategories, []
  // Mail color
  | 17, true
  // Duplicator color
  | 26, true
  // Graphic color
  | 32, true -> allColorSupplyCategories, []
  // Mail mono
  | 17, false
  // Duplicator mono
  | 26, false
  // Graphic mono
  | 32, false -> allMonoSupplyCategories, []
  // In cases where the model category isn't explicitly supported, we'll just look at the IsColor flag on the equipment
  | _, false -> monoTonerSupplyCategories, nonTonerSupplyCategories
  | _, true -> colorTonerSupplyCategories, nonTonerSupplyCategories

let determineSupplyCategoriesForEquipment (equipment : Equipment) (equipmentSupplyCategories : SupplyCategory list) =
  let xSupplyCategories, ySupplyCategories = modelCategoryAndIsColorBasedSupplyCategories equipment.ModelCategoryId equipment.IsColor
  match equipmentSupplyCategories with
  // If there aren't supply custom properties populated, we'll do our best to determine what supply categories are relevant
  | [] ->
    xSupplyCategories, ySupplyCategories
  // If there are supply custom properties populated, we'll just show those
  | _ ->
    let otherSupplyCategories = [ xSupplyCategories; ySupplyCategories ] |> List.concat |> List.distinct
    equipmentSupplyCategories, otherSupplyCategories
  |> List.sortBy(fun x -> x.toSort)

let update (msg : Msg) (model : Model) : Model * Cmd<Msg> =
  match model, msg with
  | _, ResetState ->
    EquipmentLookup, Cmd.none
  | EquipmentLookup, EquipmentLookupResult r ->
    match r with
    | Ok e -> model, Cmd.ofMsg (EquipmentSelected e)
    | Error v ->
      let failureMessage = v.toString()
      let equipmentId = v.LookupRequest.EquipmentOrSerialNumber
      DowngradedForm (Components.SuppliesForm.EntryState.init equipmentId failureMessage), Cmd.none
  | EquipmentLookup, EquipmentSelected e ->
    let cmd = Cmd.ofMsg (Msg.MpsScanRequested e)
    ContactEntry { Equipment = e; MpsScanState = MpsScanState.RequestPending; Contact = None; }, cmd
  | _, MpsScanRequested e ->
    model, lookupMpsScanCmd e.Token
  | _, MpsScanLookupResult mpsScanOption ->
    let l = mpsScanOption |> Option.map (fun x -> MpsScanFound x) |> Option.defaultValue NoMpsScanFound
    let newModel =
      match model with
      | EquipmentLookup
      | OrderResult _
      | DowngradedForm _
      | PlacingOrder _
      | ExistingOrdersWarning _ -> model
      | ContactEntry s -> ContactEntry { s with MpsScanState = l }
      | PurchaseOrderEntry s -> PurchaseOrderEntry { s with MpsScanState = l }
      | SupplyIdentification s -> SupplyIdentification { s with MpsScanState = l }
      | SupplySelection s -> SupplySelection { s with MpsScanState = l }
      | SupplyOrderInfoLookup s -> SupplyOrderInfoLookup { s with MpsScanState = l }
    newModel, Cmd.none
    //model, Cmd.none
  | ContactEntry s, ContactEntryCompleted c ->
    match s.Equipment.PurchaseOrderNumberRequired with
    | true ->
      PurchaseOrderEntry { Equipment = s.Equipment; MpsScanState = s.MpsScanState; Contact = c; PurchaseOrderNumber = None }, Cmd.none
    | false ->
      SupplyIdentification { Equipment = s.Equipment; MpsScanState = s.MpsScanState; Contact = c; PurchaseOrderNumber = None; }, lookupSupplyCategoriesCmd s.Equipment.Token
  | PurchaseOrderEntry s, PurchaseOrderNumberEntryCompleted poNumber ->
    SupplyIdentification { Equipment = s.Equipment; MpsScanState = s.MpsScanState; Contact = s.Contact; PurchaseOrderNumber = Some poNumber; }, lookupSupplyCategoriesCmd s.Equipment.Token
  | SupplyIdentification s, SupplyCategoriesLookupResult r ->
    let availableCategories, otherCategories =
      match r with
      | Error _ -> determineSupplyCategoriesForEquipment s.Equipment []
      | Ok equipmentSupplyCategories -> determineSupplyCategoriesForEquipment s.Equipment equipmentSupplyCategories
    SupplySelection { Equipment = s.Equipment; MpsScanState = s.MpsScanState; SupplyOrderInfos = None; SupplyCategories = (if availableCategories.Length = 1 then availableCategories |> List.take 1 else []); AvailableSupplyCategories = availableCategories; OtherSupplyCategories = otherCategories; Contact = s.Contact; PurchaseOrderNumber = s.PurchaseOrderNumber }, Cmd.none
  | SupplySelection s, SupplyCategoriesSelected categories ->
    let m = { s with SupplyCategories = categories }
    SupplySelection m, Cmd.none
  | SupplySelection m, LookupOrders ->
    SupplyOrderInfoLookup m, getOrderHistoryCmd (m.Equipment.Token,{ Shared.SupplyHistoryRequest.SupplyCategories = m.SupplyCategories})
  | SupplyOrderInfoLookup s, SupplyOrderInfoLookupResult orderResult ->
    match orderResult with
    | Ok orders when orders.SupplyQuoteDetails.Length > 0 || orders.SupplyOrderDetails.Length > 0 -> ExistingOrdersWarning { s with SupplyOrderInfos = Some orders; }, Cmd.none
    | Ok orders -> SupplyOrderInfoLookup { s with SupplyOrderInfos = Some orders; }, Cmd.none
    | Error v -> SupplyOrderInfoLookup { s with SupplyOrderInfos = Some { SupplyOrderDetails = []; SupplyQuoteDetails = []; };}, Cmd.none
  | ExistingOrdersWarning s, AcknowledgeExistingOrders ->
    let r =
      { Shared.SupplyRequest.EquipmentNumber = s.Equipment.EquipmentNumber
        SupplyCategories = s.SupplyCategories
        Contact = s.Contact
        MpsScan = s.MpsScanState.toOption()
        PurchaseOrderNumber = s.PurchaseOrderNumber
      }
    SupplyOrderInfoLookup s, Cmd.ofMsg (PlaceOrder r)
  | SupplyOrderInfoLookup s, PlaceOrder r ->
    PlacingOrder s, orderSuppliesCmd s.Equipment.Token r
  | PlacingOrder s, SupplyRequestResult r ->
    match r with
    | Ok o ->
      OrderResult { QueueResponse = o; SupplyRequestEntryState = s; }, Cmd.none
    | Error msg ->
      let alert =
        Cmd.Swal.fire [
            swal.title "Unable to process request"
            swal.text "Please try the alternate request form." ]
      let entryState =
        { FirstAndLastName =
            match s.Contact with
            | ContactSelectionInfo.Contact _ -> FieldState.defaultValue
            | ContactSelectionInfo.NewContact c -> FieldState.init (Some (sprintf "%s %s" c.FirstName c.LastName))
          CompanyName = FieldState.defaultValue
          ShippingAddress = FieldState.defaultValue
          EquipmentLocation = None
          EmailAddress =
            match s.Contact with
            | ContactSelectionInfo.Contact c -> FieldState.init (Some c.Email)
            | ContactSelectionInfo.NewContact c -> FieldState.init (Some c.Email)
          BusinessPhone =
            match s.Contact with
            | ContactSelectionInfo.Contact _ -> FieldState.defaultValue
            | ContactSelectionInfo.NewContact c -> FieldState.init (Some c.Phone)
          Items =
            [ { Id = System.Guid.NewGuid()
                EquipmentId = FieldState.init (Some s.Equipment.EquipmentNumber)
                MonoMeter = None
                ColorMeter = None
                Make = FieldState.defaultValue
                Model = FieldState.defaultValue
                SuppliesNeeded = FieldState.defaultValue }]
          AdditionalEntryStateDetail = AdditionalEntryStateDetail.defaultValue
          FailureMessage = Some msg }
      DowngradedForm entryState, alert
  | _ -> model, Cmd.none

let private (|Int|_|) (str: string) =
    match System.Int32.TryParse(str) with
    | true,res -> Some res
    | _ -> None

let viewContinueButton (classes : Styles.SiteClasses) (s : SupplyRequestEntryState) isLoading dispatch =
  let buttonProgressIndicator =
    Mui.circularProgress [
      prop.className classes.buttonProgress
      prop.size 24
    ]

  Mui.grid [
    grid.container true
    //grid.direction.column
    grid.spacing._2
    grid.children [
      Mui.grid [
        grid.item true
        grid.xs._12
        grid.children [
          Html.div [
            prop.className classes.wrapper
            prop.children [
              Mui.button [
                prop.type'.submit
                button.fullWidth true
                button.variant.contained
                button.color.primary
                button.children "Continue"
                button.disabled (s.SupplyCategories.Length = 0 || isLoading)
                prop.onClick (fun e ->
                  e.preventDefault()
                  LookupOrders |> dispatch
                )
              ]
              if isLoading then buttonProgressIndicator else Html.none
            ]
          ]
        ]
      ]
    ]
  ]

let viewRequestAdditionalSuppliesButtons (classes : Styles.SiteClasses) (s : SupplyRequestEntryState) isLoading dispatch =
  let buttonProgressIndicator =
    Mui.circularProgress [
      prop.className classes.buttonProgress
      prop.size 24
    ]
  Mui.grid [
      grid.container true
      //grid.direction.column
      grid.spacing._2
      grid.children [
      Mui.grid [
          grid.item true
          grid.xs._6
          grid.children [
          Html.div [
              prop.className classes.wrapper
              prop.children [
              Mui.button [
                  prop.type'.submit
                  button.fullWidth true
                  button.variant.contained
                  button.color.primary
                  button.children "Request Additional Supplies"
                  button.disabled (s.SupplyOrderInfos.IsNone || isLoading)
                  prop.onClick (fun e ->
                      e.preventDefault ()
                      AcknowledgeExistingOrders |> dispatch
                  )
              ]
              if isLoading then buttonProgressIndicator else Html.none
              ]
          ]
          ]
      ]
      Mui.grid [
          grid.item true
          grid.xs._6
          grid.children [
          Mui.button [
              button.fullWidth true
              button.variant.contained
              button.color.default'
              button.children "Clear"
              button.disabled isLoading
              prop.onClick (fun e ->
              e.preventDefault()
              ResetState |> dispatch
              )
          ]
          ]
      ]
      ]
  ]

let viewSubmitAndCancelButtons (classes : Styles.SiteClasses) (s : SupplyRequestEntryState) isLoading dispatch =
  let buttonProgressIndicator =
    Mui.circularProgress [
      prop.className classes.buttonProgress
      prop.size 24
    ]
  Mui.grid [
    grid.container true
    //grid.direction.column
    grid.spacing._2
    grid.children [
      Mui.grid [
        grid.item true
        grid.xs._6
        grid.children [
          Html.div [
            prop.className classes.wrapper
            prop.children [
              Mui.button [
                prop.type'.submit
                button.fullWidth true
                button.variant.contained
                button.color.primary
                button.children "Order"
                button.disabled (s.SupplyCategories.Length = 0 || isLoading)
                prop.onClick (fun e ->
                  e.preventDefault()
                  let r =
                    { Shared.SupplyRequest.EquipmentNumber = s.Equipment.EquipmentNumber
                      SupplyCategories = s.SupplyCategories
                      Contact = s.Contact
                      MpsScan = s.MpsScanState.toOption()
                      PurchaseOrderNumber = s.PurchaseOrderNumber
                    }
                  r |> PlaceOrder |> dispatch
                )
              ]
              if isLoading then buttonProgressIndicator else Html.none
            ]
          ]
        ]
      ]
      Mui.grid [
        grid.item true
        grid.xs._6
        grid.children [
          Mui.button [
            button.fullWidth true
            button.variant.contained
            button.color.default'
            button.children "Clear"
            button.disabled isLoading
            prop.onClick (fun e ->
              e.preventDefault()
              ResetState |> dispatch
            )
          ]
        ]
      ]
    ]
  ]

let pendingHeaderMessage =
  Mui.grid [
    grid.container true
    grid.direction.column
    grid.spacing._2
    grid.children [
      Mui.grid [
        grid.item true
        grid.children [
          Mui.typography [
            typography.variant.body1
            typography.children "Please fill out this form to request supplies."
          ]
        ]
      ]
    ]
  ]

let timelineOppositeContentStyles =
  prop.style [
    style.flexBasis (0.2)
    style.flexGrow (0)
    style.flexShrink (0)
    style.padding (0)
  ]

let viewSectionHeader (headerText : string) =
  Mui.grid [
    grid.container true
    grid.direction.column
    grid.spacing._2
    grid.children [
      Mui.grid [
        grid.item true
        grid.children [
          Mui.typography [
            typography.variant.h5
            typography.gutterBottom false
            typography.children headerText
          ]
        ]
      ]
    ]
  ]

let hideOnMobile (e : ReactElement) =
  Mui.hidden [
    hidden.smDown true
    hidden.children e
  ]

let viewSupplyOrderInfoState = React.functionComponent (fun (inputProps: {| state : SupplyRequestEntryState; showWarning : bool; dispatchFn : (Msg -> unit) |}) ->
  let classes = Styles.useStyles()
  match inputProps.state.SupplyOrderInfos with
  | None -> Html.text "Checking for pending and recent orders..."
  | Some orderInfos ->
    match orderInfos.SupplyQuoteDetails, orderInfos.SupplyOrderDetails with
    | [],[] -> Html.text "No recent orders found."
    | _ ->
      Html.div [
        let name, email =
          match inputProps.state.Contact with
          | ContactSelectionInfo.Contact v -> None, v.Email
          | ContactSelectionInfo.NewContact v -> Some (sprintf "%s %s" v.FirstName v.LastName), v.Email
        Components.SupplyOrderInfos.view {| token = inputProps.state.Equipment.Token; equipmentNumber = inputProps.state.Equipment.EquipmentNumber; orderInfos = orderInfos; customerContactName = name; customerContactEmail = email; |}
        if inputProps.showWarning then
          let msg =
            match orderInfos.SupplyQuoteDetails.Length, orderInfos.SupplyOrderDetails.Length with
            | 0,0 -> Html.none
            | 1,0
            | 0,1 -> Html.text "There is a recent request for this equipment."
            | _ -> Html.text "There are recent requests for this equipment."

          Html.div [
            prop.className classes.wrapper
            prop.children [
              Mui.alert [
                alert.severity.warning
                alert.children msg
              ]
            ]
          ]
          //Html.text "Would you like to create a new supply request?"
      ]
)

type SectionParts = { Header : ReactElement; IsActive : bool; HideOnMobile : bool; Content : ReactElement list; Icon : ReactElement; }

let equipmentIcon = printerIcon []
let contactIcon = personIcon []
let purchaseOrderNumberIcon = infoIcon []
let supplyIcon = packageVariantClosedIcon []
let orderHistoryIcon = historyIcon []
let orderIcon = truckFastIcon []

let equipmentHeader = viewSectionHeader "Equipment"
let contactHeader = viewSectionHeader "Contact"
let purchaseOrderNumberHeader = viewSectionHeader "PO Number"
let supplyHeader = viewSectionHeader "Supply Selection"
let orderHistoryHeader = viewSectionHeader "Order History"
let orderHeader = viewSectionHeader "Order Result"

let buildSections model reCaptchaSiteKey classes dispatch =
  match model with
  | EquipmentLookup ->
    [ { Header = equipmentHeader
        IsActive = true;
        HideOnMobile = false;
        Content = [ Components.EquipmentLookup.view {| reCaptchaSiteKey = reCaptchaSiteKey; equipmentOrSerialNumberOption = None; returnResult = (fun e -> e |> EquipmentLookupResult |> dispatch) |} ]
        Icon = equipmentIcon }
      { Header = contactHeader
        IsActive = false;
        HideOnMobile = true;
        Content = []
        Icon = contactIcon }
      { Header = purchaseOrderNumberHeader
        IsActive = false;
        HideOnMobile = true;
        Content = []
        Icon = purchaseOrderNumberIcon }
      { Header = supplyHeader
        IsActive = false;
        HideOnMobile = true;
        Content = []
        Icon = supplyIcon }
      { Header = orderHistoryHeader
        IsActive = false;
        HideOnMobile = true;
        Content = []
        Icon = orderHistoryIcon }
      { Header = orderHeader
        IsActive = false;
        HideOnMobile = true;
        Content = []
        Icon = orderIcon }  ]
  | ContactEntry s ->
    [ { Header = equipmentHeader
        IsActive = false;
        HideOnMobile = false;
        Content = [ Components.EquipmentInfo.view s.Equipment ]
        Icon = equipmentIcon }
      { Header = contactHeader
        IsActive = true;
        HideOnMobile = false;
        Content =
          match s.Contact with
          | None -> [ Components.ContactLookup.view {| token = s.Equipment.Token; returnResult = ContactEntryCompleted >> dispatch; |} ]
          | Some c -> [ Components.ContactInfo.view c ]
        Icon = contactIcon }
      { Header = purchaseOrderNumberHeader
        IsActive = false;
        HideOnMobile = true;
        Content = []
        Icon = purchaseOrderNumberIcon }
      { Header = supplyHeader
        IsActive = false;
        HideOnMobile = true;
        Content = []
        Icon = supplyIcon }
      { Header = orderHistoryHeader
        IsActive = false;
        HideOnMobile = true;
        Content = []
        Icon = orderHistoryIcon }
      { Header = orderHeader
        IsActive = false;
        HideOnMobile = true;
        Content = []
        Icon = orderIcon } ]
  | PurchaseOrderEntry s ->
    [ { Header = equipmentHeader
        IsActive = false;
        HideOnMobile = false;
        Content = [ Components.EquipmentInfo.view s.Equipment ]
        Icon = equipmentIcon }
      { Header = contactHeader
        IsActive = false;
        HideOnMobile = false;
        Content = [ Components.ContactInfo.view s.Contact ]
        Icon = contactIcon }
      { Header = purchaseOrderNumberHeader
        IsActive = true;
        HideOnMobile = false;
        Content = [ Components.PurchaseOrderNumberEntry.view {| returnResult = PurchaseOrderNumberEntryCompleted >> dispatch |} ]
        Icon = purchaseOrderNumberIcon }
      { Header = supplyHeader
        IsActive = false;
        HideOnMobile = true;
        Content = []
        Icon = supplyIcon }
      { Header = orderHistoryHeader
        IsActive = false;
        HideOnMobile = true;
        Content = []
        Icon = orderHistoryIcon }
      { Header = orderHeader
        IsActive = false;
        HideOnMobile = true;
        Content = []
        Icon = orderIcon } ]
  | SupplyIdentification s ->
    [ { Header = equipmentHeader
        IsActive = false;
        HideOnMobile = false;
        Content = [ Components.EquipmentInfo.view s.Equipment ]
        Icon = equipmentIcon }
      { Header = contactHeader
        IsActive = false;
        HideOnMobile = false;
        Content = [ Components.ContactInfo.view s.Contact ]
        Icon = contactIcon }
      { Header = purchaseOrderNumberHeader
        IsActive = false;
        HideOnMobile = false;
        Content =
          s.PurchaseOrderNumber
          |> Option.defaultValue "N/A"
          |> (fun x -> [ Components.PurchaseOrderNumberInfo.view x ])
        Icon = purchaseOrderNumberIcon }
      { Header = supplyHeader
        IsActive = true;
        HideOnMobile = false;
        Content =
          [ Html.text "Identifying supplies for equipment..." ]
        Icon = supplyIcon }
      { Header = orderHistoryHeader
        IsActive = false;
        HideOnMobile = true;
        Content = []
        Icon = orderHistoryIcon }
      { Header = orderHeader
        IsActive = false;
        HideOnMobile = true;
        Content = []
        Icon = orderIcon } ]
  | SupplySelection s ->
    [ { Header = equipmentHeader
        IsActive = false;
        HideOnMobile = false;
        Content = [ Components.EquipmentInfo.view s.Equipment ]
        Icon = equipmentIcon }
      { Header = contactHeader
        IsActive = false;
        HideOnMobile = false;
        Content = [ Components.ContactInfo.view s.Contact ]
        Icon = contactIcon }
      { Header = purchaseOrderNumberHeader
        IsActive = false;
        HideOnMobile = false;
        Content =
          s.PurchaseOrderNumber
          |> Option.defaultValue "N/A"
          |> (fun x -> [ Components.PurchaseOrderNumberInfo.view x ])
        Icon = purchaseOrderNumberIcon }
      { Header = supplyHeader
        IsActive = true;
        HideOnMobile = false;
        Content =
          [ Components.SupplyCategories.view {| availableSelections = s.AvailableSupplyCategories; otherSelections = s.OtherSupplyCategories; initialSelections = s.SupplyCategories; mpsScanOption = s.MpsScanState.toOption (); disableInput = false; returnResult = (SupplyCategoriesSelected >> dispatch) |}
            viewContinueButton classes s false dispatch ]
        Icon = supplyIcon }
      { Header = orderHistoryHeader
        IsActive = false;
        HideOnMobile = true;
        Content = []
        Icon = orderHistoryIcon }
      { Header = orderHeader
        IsActive = false;
        HideOnMobile = true;
        Content = []
        Icon = orderIcon } ]
  | ExistingOrdersWarning s ->
    [ { Header = equipmentHeader
        IsActive = false;
        HideOnMobile = false;
        Content = [ Components.EquipmentInfo.view s.Equipment ]
        Icon = equipmentIcon }
      { Header = contactHeader
        IsActive = false;
        HideOnMobile = false;
        Content = [ Components.ContactInfo.view s.Contact ]
        Icon = contactIcon }
      { Header = purchaseOrderNumberHeader
        IsActive = false;
        HideOnMobile = false;
        Content =
          s.PurchaseOrderNumber
          |> Option.defaultValue "N/A"
          |> (fun x -> [ Components.PurchaseOrderNumberInfo.view x ])
        Icon = purchaseOrderNumberIcon }
      { Header = supplyHeader
        IsActive = true;
        HideOnMobile = false;
        Content =
          [ Components.SupplyCategories.view {| availableSelections = s.AvailableSupplyCategories; otherSelections = s.OtherSupplyCategories; initialSelections = s.SupplyCategories; mpsScanOption = s.MpsScanState.toOption (); disableInput = true; returnResult = (SupplyCategoriesSelected >> dispatch) |}]
        Icon = supplyIcon }
      { Header = orderHistoryHeader
        IsActive = true;
        HideOnMobile = false;
        Content =
          [ viewSupplyOrderInfoState {| state = s; showWarning = true; dispatchFn = dispatch; |}
            viewRequestAdditionalSuppliesButtons classes s false dispatch ]
        Icon = orderHistoryIcon }
      { Header = orderHeader
        IsActive = false;
        HideOnMobile = true;
        Content = []
        Icon = orderIcon } ]
  | SupplyOrderInfoLookup s ->
    [ { Header = equipmentHeader
        IsActive = false;
        HideOnMobile = false;
        Content = [ Components.EquipmentInfo.view s.Equipment ]
        Icon = equipmentIcon }
      { Header = contactHeader
        IsActive = false;
        HideOnMobile = false;
        Content = [ Components.ContactInfo.view s.Contact ]
        Icon = contactIcon }
      { Header = purchaseOrderNumberHeader
        IsActive = false;
        HideOnMobile = false;
        Content =
          s.PurchaseOrderNumber
          |> Option.defaultValue "N/A"
          |> (fun x -> [ Components.PurchaseOrderNumberInfo.view x ])
        Icon = purchaseOrderNumberIcon }
      { Header = supplyHeader
        IsActive = true;
        HideOnMobile = false;
        Content =
          [ Components.SupplyCategories.view {| availableSelections = s.AvailableSupplyCategories; otherSelections = s.OtherSupplyCategories; initialSelections = s.SupplyCategories; mpsScanOption = s.MpsScanState.toOption (); disableInput = true; returnResult = (SupplyCategoriesSelected >> dispatch) |}]
        Icon = supplyIcon }
      { Header = orderHistoryHeader
        IsActive = false;
        HideOnMobile = false;
        Content =
          [ viewSupplyOrderInfoState {| state = s; showWarning = false; dispatchFn = dispatch; |}
            viewSubmitAndCancelButtons classes s false dispatch ]
        Icon = orderHistoryIcon }
      { Header = orderHeader
        IsActive = false;
        HideOnMobile = true;
        Content = []
        Icon = orderIcon } ]
  | PlacingOrder s ->
    [ { Header = equipmentHeader
        IsActive = false;
        HideOnMobile = false;
        Content = [ Components.EquipmentInfo.view s.Equipment ]
        Icon = equipmentIcon }
      { Header = contactHeader
        IsActive = false;
        HideOnMobile = false;
        Content = [ Components.ContactInfo.view s.Contact ]
        Icon = contactIcon }
      { Header = purchaseOrderNumberHeader
        IsActive = false;
        HideOnMobile = false;
        Content =
          s.PurchaseOrderNumber
          |> Option.defaultValue "N/A"
          |> (fun x -> [ Components.PurchaseOrderNumberInfo.view x ])
        Icon = purchaseOrderNumberIcon }
      { Header = supplyHeader
        IsActive = true;
        HideOnMobile = false;
        Content =
          [ Components.SupplyCategories.view {| availableSelections = s.AvailableSupplyCategories; otherSelections = s.OtherSupplyCategories; initialSelections = s.SupplyCategories; mpsScanOption = s.MpsScanState.toOption (); disableInput = true; returnResult = (SupplyCategoriesSelected >> dispatch) |}]
        Icon = supplyIcon }
      { Header = orderHistoryHeader
        IsActive = false;
        HideOnMobile = false;
        Content =
          [ viewSupplyOrderInfoState {| state = s; showWarning = false; dispatchFn = dispatch; |}
            match s.SupplyOrderInfos with
            | None -> Html.none
            | Some orderInfos when orderInfos.SupplyQuoteDetails.Length = 0 && orderInfos.SupplyOrderDetails.Length = 0 -> viewSubmitAndCancelButtons classes s true dispatch
            | _ -> viewRequestAdditionalSuppliesButtons classes s true dispatch ]
        Icon = orderHistoryIcon }
      { Header = orderHeader
        IsActive = false;
        HideOnMobile = true;
        Content = []
        Icon = orderIcon } ]
  | OrderResult r ->
    [ { Header = equipmentHeader
        IsActive = false;
        HideOnMobile = false;
        Content = [ Components.EquipmentInfo.view r.SupplyRequestEntryState.Equipment ]
        Icon = equipmentIcon }
      { Header = contactHeader
        IsActive = false;
        HideOnMobile = false;
        Content = [ Components.ContactInfo.view r.SupplyRequestEntryState.Contact ]
        Icon = contactIcon }
      { Header = purchaseOrderNumberHeader
        IsActive = false;
        HideOnMobile = false;
        Content =
          r.SupplyRequestEntryState.PurchaseOrderNumber
          |> Option.defaultValue "N/A"
          |> (fun x -> [ Components.PurchaseOrderNumberInfo.view x ])
        Icon = purchaseOrderNumberIcon }
      { Header = supplyHeader
        IsActive = false;
        HideOnMobile = false;
        Content = [ Components.SupplyCategories.view {| availableSelections = r.SupplyRequestEntryState.AvailableSupplyCategories; otherSelections = r.SupplyRequestEntryState.OtherSupplyCategories; initialSelections = r.SupplyRequestEntryState.SupplyCategories; mpsScanOption = r.SupplyRequestEntryState.MpsScanState.toOption (); disableInput = true; returnResult = (SupplyCategoriesSelected >> dispatch) |} ]
        Icon = supplyIcon }
      { Header = orderHistoryHeader
        IsActive = false;
        HideOnMobile = false;
        Content = [ viewSupplyOrderInfoState {| state = r.SupplyRequestEntryState; showWarning = false; dispatchFn = dispatch; |} ]
        Icon = orderHistoryIcon }
      { Header = orderHeader
        IsActive = false;
        HideOnMobile = false;
        Content =
          [ Html.text (sprintf "Thank you. Your supply request has been received, and you will receive a notification when your request has been processed.")
            Mui.button [
              prop.type'.submit
              button.fullWidth true
              button.variant.contained
              button.color.primary
              button.children "Add Another Request"
              prop.onClick (fun e ->
                e.preventDefault ()
                ResetState |> dispatch
              )
            ]
          ]
        Icon = orderIcon } ]
  | DowngradedForm _ ->
    []

let viewMobile (sections : SectionParts list) =
  Html.div [
    prop.children (
      sections
      |> List.filter (fun x -> not x.HideOnMobile)
      |> List.collect (fun x ->
        [
          x.Header
          yield! x.Content
        ]
      )
    )
  ]

let viewNonMobile (sections : SectionParts list) =
  Mui.timeline [
    timeline.children (
      sections
      |> List.mapi (fun i x ->
        Mui.timelineItem [
          Mui.timelineOppositeContent [
            //prop.className classes.timelineOppositeContent
            timelineOppositeContentStyles
          ]
          Mui.timelineSeparator [
            Mui.timelineDot [
              if x.IsActive then timelineDot.color.primary
              timelineDot.children [ x.Icon ]
            ]
            if (i + 1) < sections.Length then
              Mui.timelineConnector [
              ]
          ]
          Mui.timelineContent [
            x.Header
            yield! x.Content
          ]
        ]
      )
    )
  ]

let view = React.functionComponent (fun (inputProps: {| equipmentOption : Equipment option; reCaptchaSiteKey : string; |}) ->
  let model, dispatch = React.useElmish(init (inputProps.equipmentOption), update, [| |])
  let classes = Styles.useStyles()
  let isMobile = Hooks.useMediaQuery (Client.Theme.defaultTheme.breakpoints.downSm)
  Html.div [
    prop.className [
      classes.standAloneContent
    ]
    prop.children [
      Mui.grid [
        grid.container true
        grid.direction.column
        grid.spacing._2
        grid.children [
          Mui.grid [
            grid.item true
            grid.children [
              Mui.typography [
                typography.variant.h4
                typography.children "Request Supplies"
              ]
            ]
          ]
        ]
      ]

      match model with
      | DowngradedForm initialState -> Components.SuppliesForm.view {| reCaptchaSiteKey = inputProps.reCaptchaSiteKey; initialValues = Some initialState ; returnResult = (fun _ -> dispatch ResetState); |}
      | _ ->
        pendingHeaderMessage
        let sections = buildSections model inputProps.reCaptchaSiteKey classes dispatch
        match isMobile with
        | true -> viewMobile sections
        | false -> viewNonMobile sections
    ]
  ]
)